import { useState, type FC } from 'react'
import { Dropdown } from 'react-bootstrap'
import { DEFAULT_PAGE_SIZE } from '../../../common/constants'
import { CustomPaginationComponent } from '../../../components/DataTable/CustomPaginationComponent'
import { Spinner } from '../../../components/Spinner/Spinner'
import { BookingStatus, type Booking, type SchedulingEventType, type SchedulingFilters } from '../../../models/Scheduling'
import { selectSafeAuthenticatedUser } from '../../../store/auth/authSlice'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import {
  useArchiveBookingMutation,
  useGetBookingsQuery,
  useGetSchedulingEventTypesQuery,
  useGetSchedulingLocationsQuery,
} from '../../../store/scheduling/scheduling'
import { addToast } from '../../../store/toast/toastSlice'
import { TrackEvent, useTracking } from '../../../apps/professional-app/fa/common/tracking'
import { formatDateAndTime, formatDateAndTimeRange, getBookingLocationLine } from '../bookingFormat'
import { toBookingsFilterArgs } from '../bookingTabs'
import { getClientCancellationAuthor } from '../history/historyDisplay'
import { BookingActionMenu } from '../rows/BookingActionMenu'
import { BookingRow } from '../rows/BookingRow'
import { ContactChips } from '../rows/ContactChips'
import { EventTypeChip } from '../rows/EventTypeChip'
import { ProviderBadge } from '../rows/ProviderBadge'
import { SchedulingEmptyState } from './SchedulingEmptyState'
import { SchedulingTabErrorCard } from './SchedulingTabErrorCard'

// Only client-actor cancels appear under status=cancelled — the backend archives every
// user-actor cancel (advisor, archive/dismiss, calendar-sync) straight to History.
const CancelledDateBlock: FC<{ booking: Booking }> = ({ booking }) => (
  <div className='d-flex flex-column flex-1 text-nowrap'>
    <div className='lead-small'>Canceled by client</div>
    <div className='paragraph-small zocks-text-gray-700'>{formatDateAndTime(new Date(booking.closedAt ?? booking.updatedAt))}</div>
  </div>
)

const CancelledSummary: FC<{ booking: Booking; eventType?: SchedulingEventType; address?: string }> = ({ booking, eventType, address }) => (
  <div className='d-flex flex-column flex-4 mw-0 zocks-gap-0.25'>
    <div className='bold text-truncate'>{booking.title ?? eventType?.name}</div>
    <div className='d-flex align-items-center flex-wrap zocks-gap-0.5'>
      <ContactChips clients={booking.clients} markGuests />
      {eventType ? (
        <>
          <span className='zocks-text-gray-600'>·</span>
          <EventTypeChip eventType={eventType} />
        </>
      ) : null}
      <ProviderBadge meetingMode={booking.meetingMode} meetingUrl={booking.meetingUrl} />
    </div>
    {address ? (
      <div className='d-flex align-items-center zocks-gap-0.25 paragraph-small zocks-text-gray-500'>
        <i className='bi bi-geo-alt' aria-hidden='true' />
        {address}
      </div>
    ) : null}
    {booking.start ? <div className='paragraph-small zocks-text-gray-500'>was {formatDateAndTimeRange(booking.start, booking.end)}</div> : null}
  </div>
)

export const CancelledTab: FC<{ filters: SchedulingFilters }> = ({ filters }) => {
  const dispatch = useAppDispatch()
  const { trackingClient } = useTracking()
  const authenticatedUser = useAppSelector(selectSafeAuthenticatedUser)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const {
    data: bookingsResponse,
    isFetching,
    isError,
    refetch,
  } = useGetBookingsQuery({
    ...toBookingsFilterArgs(filters),
    accountId: authenticatedUser.accountId,
    status: BookingStatus.CANCELLED,
    pageRequest: { page: page - 1, size: pageSize },
  })
  const bookings = bookingsResponse?.data
  const totalElements = bookingsResponse?.page.totalElements ?? 0
  const { data: eventTypes } = useGetSchedulingEventTypesQuery({ accountId: authenticatedUser.accountId })
  const { data: locations } = useGetSchedulingLocationsQuery({ accountId: authenticatedUser.accountId })
  const [archiveBooking] = useArchiveBookingMutation()

  if (isFetching) {
    return (
      <div className='d-flex justify-content-center py-5'>
        <Spinner />
      </div>
    )
  }

  if (isError) {
    return <SchedulingTabErrorCard title="Couldn't load canceled meetings" onRetry={refetch} />
  }

  if (!Array.isArray(bookings) || bookings.length === 0) {
    return <SchedulingEmptyState title='No canceled meetings' subtitle='Meetings canceled by clients appear here until you archive them.' />
  }

  const eventTypeById = new Map((eventTypes ?? []).map((eventType) => [eventType.id, eventType]))
  const locationById = new Map((locations ?? []).map((location) => [location.id, location]))

  const handleArchiveBooking = async (booking: Booking) => {
    const bookingId = booking.bookingId
    const result = await archiveBooking({ accountId: authenticatedUser.accountId, bookingId })
    if ('error' in result) {
      dispatch(addToast({ variant: 'error', title: 'Could not archive the booking', messages: ['Please try again.'] }))
      return
    }
    trackingClient.track({ eventName: TrackEvent.SCHEDULING_BOOKING_ARCHIVED, eventDetails: { bookingId, source: 'cancelled' } })
    dispatch(addToast({ variant: 'success', title: 'Booking archived', messages: [`${booking.title ?? 'The booking'} is now in History.`] }))
  }

  return (
    <div className='d-flex flex-column gap-2'>
      {bookings.map((booking) => {
        const location = booking.location?.id ? locationById.get(booking.location.id) : undefined
        return (
          <BookingRow
            key={booking.bookingId}
            booking={booking}
            location={location}
            detailsVariant='history'
            detailsCancellationAuthor={getClientCancellationAuthor(booking)}
            timeBlock={<CancelledDateBlock booking={booking} />}
            summary={
              <CancelledSummary
                booking={booking}
                eventType={booking.eventTypeId ? eventTypeById.get(booking.eventTypeId) : undefined}
                address={getBookingLocationLine(booking, location)}
              />
            }
          >
            <BookingActionMenu ariaLabel={`${booking.title ?? 'Booking'} actions`}>
              <Dropdown.Item onClick={() => handleArchiveBooking(booking)}>
                <i className='bi bi-archive me-1' /> Archive
              </Dropdown.Item>
            </BookingActionMenu>
          </BookingRow>
        )
      })}
      <CustomPaginationComponent
        rowsPerPage={pageSize}
        rowCount={totalElements}
        currentPage={page}
        onChangePage={setPage}
        onChangeRowsPerPage={(newPageSize) => {
          setPage(1)
          setPageSize(newPageSize)
        }}
      />
    </div>
  )
}
